import { useEffect, useMemo, useRef, useState } from 'react';
import { bossDisplayName } from '../../../shared/domain/bossPresentation.js';
import { DororaidIcon } from '../../../shared/ui/DororaidIcon.jsx';
import '../../../styles/raid-practice-editor-feedback.css';
import { RecommendedDeckLoadDialog } from '../../recommended-decks/shared/RecommendedDeckLoadDialog.jsx';
import { RaidPracticeDeckPicker } from './RaidPracticeDeckPicker.jsx';
import { RAID_PRACTICE_ACTION_LABELS, practiceActionStatus, practiceSlotDeleteConfirmation } from './raidPracticeActionLabels.js';

function createForm(entry, slotNo) {
  const manual = entry?.manual || null;
  const actual = entry?.actual || null;
  const initialDeck = manual?.deck?.length ? manual.deck : actual?.deck || [];
  return { slotNo: Number(manual?.slotNo || entry?.slotNo || slotNo || actual?.attackNo || 1), title: String(manual?.title || ''), damageEok: String(manual?.damageEok ?? actual?.damageEok ?? ''), used: Boolean(actual || manual?.used), deck: Array.from({ length: 5 }, (_, index) => String(initialDeck[index] || '')) };
}
function formatEok(value) { return `${Number(value || 0).toLocaleString('ko-KR', { maximumFractionDigits: 3 })}억`; }
function resolveMemberOpenid(entry, explicitValue) {
  const direct = String(explicitValue || entry?.manual?.memberOpenid || entry?.manual?.member_openid || '').trim();
  if (direct) return direct;
  const key = String(entry?.key || '');
  return key.startsWith('new:') ? key.split(':')[1] || '' : '';
}

export function RaidPracticeEditor({ entry = null, slotNo = 1, boss, seasonNo, nickname, unionId = '', memberOpenid = '', mobile = false, context = '', working = '', notice = null, onSave, onDelete, onClose, onRecommendation = null }) {
  const [form, setForm] = useState(() => createForm(entry, slotNo));
  const [localError, setLocalError] = useState('');
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [recommendationOpen, setRecommendationOpen] = useState(false);
  const damageInputRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const manual = entry?.manual || null;
  const actual = entry?.actual || null;
  const practiceMemberOpenid = resolveMemberOpenid(entry, memberOpenid);
  const isSaving = working === `save:${form.slotNo}`;
  const isDeleting = working === `delete:${form.slotNo}`;
  const deckCodes = useMemo(() => form.deck.map((code) => String(code || '').trim()).filter(Boolean), [form.deck]);
  const bossName = bossDisplayName(boss);
  const remoteError = submitAttempted && notice?.type === 'error' ? String(notice.message || '') : '';
  const visibleError = remoteError || localError;
  const contextClass = context === 'admin' ? 'admin-member-editor' : '';

  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  useEffect(() => { setForm(createForm(entry, slotNo)); setLocalError(''); setSubmitAttempted(false); setRecommendationOpen(false); }, [entry?.key, manual?.updatedAt, slotNo]);
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previousOverflow; };
  }, []);
  useEffect(() => {
    const closeByEscape = (event) => { if (event.key !== 'Escape') return; if (recommendationOpen) setRecommendationOpen(false); else onCloseRef.current?.(); };
    window.addEventListener('keydown', closeByEscape);
    return () => { window.removeEventListener('keydown', closeByEscape); };
  }, [recommendationOpen]);

  const update = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setLocalError('');
    setSubmitAttempted(false);
  };
  const focusDamageInput = () => {
    window.requestAnimationFrame(() => {
      damageInputRef.current?.focus();
      damageInputRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
  };
  const submit = async () => {
    if (isSaving || isDeleting) return;
    setSubmitAttempted(true);
    const damage = Number(form.damageEok);
    if (!Number.isFinite(damage) || damage <= 0) {
      setLocalError('딜량을 0보다 크게 입력해주세요.');
      focusDamageInput();
      return;
    }
    if (new Set(deckCodes.map((code) => code.toLocaleLowerCase('ko-KR'))).size !== deckCodes.length) {
      setLocalError('같은 덱에서 동일한 니케를 중복 선택할 수 없습니다.');
      return;
    }
    setLocalError('');
    try {
      const ok = await onSave?.({ slotNo: Number(form.slotNo), title: form.title, damageEok: damage, used: Boolean(form.used), deck: deckCodes });
      if (ok) onCloseRef.current?.();
      else setLocalError('모의전 기록을 저장하지 못했습니다. 표시된 오류를 확인해주세요.');
    } catch (error) {
      setLocalError(error instanceof Error ? error.message : '모의전 기록을 저장하지 못했습니다.');
    }
  };
  const remove = async () => {
    if (!manual?.id || isSaving || isDeleting || !window.confirm(practiceSlotDeleteConfirmation(form.slotNo))) return;
    setSubmitAttempted(true);
    setLocalError('');
    try {
      const ok = await onDelete?.(manual.id, form.slotNo);
      if (ok) onCloseRef.current?.();
      else setLocalError('모의전 기록을 삭제하지 못했습니다. 표시된 오류를 확인해주세요.');
    } catch (error) {
      setLocalError(error instanceof Error ? error.message : '모의전 기록을 삭제하지 못했습니다.');
    }
  };
  const openRecommendation = () => { if (onRecommendation) { onRecommendation({ boss, slotNo: form.slotNo, deck: form.deck, onApply: (deck) => update('deck', deck) }); return; } setRecommendationOpen(true); };
  const saveLabel = practiceActionStatus(RAID_PRACTICE_ACTION_LABELS.save, isSaving);

  return <div className={`raid-practice-editor-overlay ${mobile ? 'mobile' : 'desktop'} ${contextClass}`.trim()} role="presentation">
    <section className="raid-practice-editor-v2" role="dialog" aria-modal="true" aria-label={`${bossName} 모의전 입력`}>
      <header className="raid-practice-editor-v2-head"><div><h2>{nickname || '내'} · {bossName}<br className="mobile-only-break" /> 모의전 입력</h2></div><div><button type="button" className="ui-icon-action save" disabled={isSaving || isDeleting} onClick={submit} aria-label={saveLabel} title={saveLabel}><DororaidIcon name="save" size={20} /></button><button type="button" className="ui-icon-action close" onClick={onClose} aria-label="닫기" title="닫기"><DororaidIcon name="close" size={20} /></button></div></header>
      {visibleError ? <p className="raid-practice-editor-v2-alert" role="alert"><span>저장 확인</span><strong>{visibleError}</strong></p> : null}
      <div className="raid-practice-editor-v2-scroll">
        <section className="raid-practice-editor-v2-form-card"><label className="title"><span>제목</span><input value={form.title} maxLength={100} onChange={(event) => update('title', event.target.value)} placeholder="고점덱 / 막타덱" /></label><div className="raid-practice-editor-v2-meta"><label><span>슬롯</span><input value={form.slotNo} readOnly /></label><label><span>딜량(억)</span><input ref={damageInputRef} type="number" min="0.000001" step="0.001" inputMode="decimal" value={form.damageEok} onChange={(event) => update('damageEok', event.target.value)} placeholder="0" /></label></div><label className="used"><input type="checkbox" checked={form.used} onChange={(event) => update('used', event.target.checked)} /><span>공격완료</span></label></section>
        {actual ? <section className="raid-practice-editor-v2-actual"><div><span>실전 단계</span><strong>{actual.phaseNo || '-'}단</strong></div><div><span>실전 딜량</span><strong>{formatEok(actual.damageEok)}</strong></div><div><span>막타</span><strong>{actual.isKill ? '예' : '아니오'}</strong></div></section> : null}
        <button type="button" className="raid-practice-editor-v2-recommend" onClick={openRecommendation}>추천 스쿼드</button>
        <section className="raid-practice-editor-v2-squad"><h3>니케 선택</h3><RaidPracticeDeckPicker value={form.deck} onChange={(deck) => update('deck', deck)} mobile={mobile} memberOpenid={practiceMemberOpenid} /></section>
        <div className="raid-practice-editor-v2-context"><span>{seasonNo}차</span><span>{bossName}</span><span>{boss?.element || '-'}</span></div>
        {manual?.id ? <button type="button" className="raid-practice-editor-v2-delete ui-icon-action danger" disabled={isSaving || isDeleting} onClick={remove} aria-label={isDeleting ? '삭제 중' : '삭제'} title={isDeleting ? '삭제 중…' : '삭제'}><DororaidIcon name="trash" size={20} /></button> : null}
      </div>
    </section>
    {recommendationOpen ? <RecommendedDeckLoadDialog mobile={mobile} unionId={unionId} defaultElement={boss?.element || '전체'} onApply={(deck) => update('deck', deck)} onClose={() => setRecommendationOpen(false)} /> : null}
  </div>;
}
